export type HistorySyncStatus =
  | 'REQUESTED'
  | 'IN_PROGRESS'
  | 'COMPLETED'
  | 'FAILED'
  | 'REFUSED';

export const RE_ONBOARD_STEPS =
  'On the phone, open WhatsApp Business → Settings → Account → Business Platform → Disconnect Account, then connect this number here again. Meta allows the history import only once per connection.';

export const CONNECTION_CHECK_STEPS =
  "Check the phone's internet connection and keep WhatsApp Business open — Meta sends the rest as the connection allows.";

export const HISTORY_SYNC_WINDOW_HOURS = 24;
export const HISTORY_SYNC_WINDOW_MS = HISTORY_SYNC_WINDOW_HOURS * 60 * 60 * 1000;

const IN_FLIGHT_STATUSES = new Set<string>(['REQUESTED', 'IN_PROGRESS']);

export const HISTORY_SYNC_STALE_MS = 15 * 60 * 1000;

export interface HistorySyncAccountLike {
  connection_type?: string | null;
  coexistence_onboarded_at?: string | null;
  history_sync_status?: string | null;
  history_sync_requested_at?: string | null;
  history_sync_progress?: number | null;
  history_sync_progress_at?: string | null;
  history_sync_completed_at?: string | null;
  history_sync_error?: string | null;
}

export function toDateOrNull(value: unknown): Date | null {
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return null;
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  const d = new Date(trimmed);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function resolveHistorySyncProgress(raw: unknown): number | null {
  if (raw === null || raw === undefined || raw === '') return null;
  if (typeof raw !== 'number' && typeof raw !== 'string') return null;
  const n = Number(raw);
  if (!Number.isFinite(n)) return null;
  return Math.min(100, Math.max(0, Math.round(n)));
}

export interface HistorySyncWindow {
  known: boolean;
  expiresAt: Date | null;
  msRemaining: number | null;
  expired: boolean;
}

export function resolveHistorySyncWindow(
  onboardedAt: unknown,
  now: Date = new Date()
): HistorySyncWindow {
  const started = toDateOrNull(onboardedAt);
  if (!started) {
    return { known: false, expiresAt: null, msRemaining: null, expired: false };
  }
  const expiresAt = new Date(started.getTime() + HISTORY_SYNC_WINDOW_MS);
  const msRemaining = expiresAt.getTime() - now.getTime();
  return {
    known: true,
    expiresAt,
    msRemaining: Math.max(0, msRemaining),
    expired: msRemaining <= 0,
  };
}

export function formatWindowRemaining(ms: number | null | undefined): string | null {
  if (typeof ms !== 'number' || !Number.isFinite(ms)) return null;
  if (ms <= 0) return null;
  const totalMinutes = Math.floor(ms / 60000);
  if (totalMinutes < 1) return 'under a minute';
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours <= 0) return `${minutes}m`;
  if (minutes <= 0) return `${hours}h`;
  return `${hours}h ${minutes}m`;
}

function joinSentences(...parts: Array<string | null | undefined>): string {
  return parts
    .filter((p): p is string => typeof p === 'string' && p.trim().length > 0)
    .map((p) => {
      const t = p.trim();
      return /[.!?]$/.test(t) ? t : `${t}.`;
    })
    .join(' ');
}

function isRateLimitText(detail?: string | null): boolean {
  if (typeof detail !== 'string') return false;
  return /request limit reached|rate limit|\(#4\)|error 4/i.test(detail);
}

export function isHistorySyncRunning(status?: string | null): boolean {
  return typeof status === 'string' && IN_FLIGHT_STATUSES.has(status);
}

export function isHistorySyncComplete(status?: string | null): boolean {
  return status === 'COMPLETED';
}

export interface HistorySyncStaleness {
  stale: boolean;
  sinceMs: number | null;
  measuredFrom: 'progress' | 'request' | null;
}

export function resolveHistorySyncStaleness(
  account: HistorySyncAccountLike | null | undefined,
  now: Date = new Date()
): HistorySyncStaleness {
  if (!account || !isHistorySyncRunning(account.history_sync_status)) {
    return { stale: false, sinceMs: null, measuredFrom: null };
  }
  const lastProgress = toDateOrNull(account.history_sync_progress_at);
  const asked = toDateOrNull(account.history_sync_requested_at);
  const anchor = lastProgress || asked;
  if (!anchor) return { stale: false, sinceMs: null, measuredFrom: null };

  const sinceMs = Math.max(0, now.getTime() - anchor.getTime());
  return {
    stale: sinceMs >= HISTORY_SYNC_STALE_MS,
    sinceMs,
    measuredFrom: lastProgress ? 'progress' : 'request',
  };
}

export function isHistorySyncActivelyImporting(
  account: HistorySyncAccountLike | null | undefined,
  now: Date = new Date()
): boolean {
  if (!account || !isHistorySyncRunning(account.history_sync_status)) return false;
  return !resolveHistorySyncStaleness(account, now).stale;
}

export const HISTORY_SYNC_WATCH_LIMIT_MS = 2 * 60 * 60 * 1000;

export type HistorySyncPollTier = 'fast' | 'slow' | 'off';

export function resolveHistorySyncPollTier(
  account: HistorySyncAccountLike | null | undefined,
  now: Date = new Date()
): HistorySyncPollTier {
  if (!account || !isHistorySyncRunning(account.history_sync_status)) return 'off';

  const staleness = resolveHistorySyncStaleness(account, now);
  if (staleness.sinceMs === null) return 'off';
  if (!staleness.stale) return 'fast';
  return staleness.sinceMs < HISTORY_SYNC_WATCH_LIMIT_MS ? 'slow' : 'off';
}

export function historySyncNeedsClock(
  account: HistorySyncAccountLike | null | undefined,
  now: Date = new Date()
): boolean {
  if (!account || !isHistorySyncEligible(account.connection_type)) return false;
  if (isHistorySyncComplete(account.history_sync_status)) return false;

  const syncWindow = resolveHistorySyncWindow(account.coexistence_onboarded_at, now);
  if (syncWindow.known && !syncWindow.expired) return true;

  return resolveHistorySyncPollTier(account, now) !== 'off';
}

export function formatStalledFor(ms: number | null | undefined): string | null {
  if (typeof ms !== 'number' || !Number.isFinite(ms) || ms <= 0) return null;
  const minutes = Math.floor(ms / 60000);
  if (minutes < 1) return null;
  if (minutes < 60) return `for ${minutes} minute${minutes === 1 ? '' : 's'}`;
  return 'for over an hour';
}

export function isHistorySyncEligible(connectionType?: string | null): boolean {
  return connectionType === 'coexistence';
}

export type HistorySyncTone = 'info' | 'warning' | 'error';

export interface HistorySyncNotice {
  tone: HistorySyncTone;
  headline: string;
  guidance: string;
  showSync: boolean;
  syncLabel: string;
  progress: number | null;
  remaining: string | null;
  window: HistorySyncWindow;
  status: HistorySyncStatus | null;
}

export function buildHistorySyncNotice(
  account: HistorySyncAccountLike | null | undefined,
  now: Date = new Date()
): HistorySyncNotice | null {
  if (!account) return null;
  if (!isHistorySyncEligible(account.connection_type)) return null;

  const status = (typeof account.history_sync_status === 'string'
    ? account.history_sync_status
    : null) as HistorySyncStatus | null;

  if (isHistorySyncComplete(status)) return null;

  const syncWindow = resolveHistorySyncWindow(account.coexistence_onboarded_at, now);

  const windowClosed = syncWindow.known && syncWindow.expired;

  const remaining = formatWindowRemaining(syncWindow.msRemaining);
  const progress = resolveHistorySyncProgress(account.history_sync_progress);

  const staleness = resolveHistorySyncStaleness(account, now);
  if (staleness.stale) {
    const stalledFor = formatStalledFor(staleness.sinceMs);
    return {
      tone: 'warning',
      headline:
        progress === null
          ? 'Chat history import appears to be stuck'
          : `Chat history import appears to be stuck at ${progress}%`,
      guidance:
        (progress === null
          ? `No conversations have arrived${stalledFor ? ` ${stalledFor}` : ''}. `
          : `${progress}% arrived, then nothing${stalledFor ? ` ${stalledFor}` : ' for a while'}. What did import is safe and stays in the inbox. `) +
        `${CONNECTION_CHECK_STEPS} ` +
        `If progress still does not resume: ${RE_ONBOARD_STEPS}`,
      showSync: false,
      syncLabel: '',
      progress: null,
      remaining,
      window: syncWindow,
      status,
    };
  }

  if (isHistorySyncRunning(status)) {
    return {
      tone: 'info',
      headline:
        progress === null
          ? 'Importing chat history from the WhatsApp Business app'
          : `Importing chat history — ${progress}% done`,
      guidance:
        'Conversations are arriving from Meta in batches — this can take several minutes. Ask the customer to keep WhatsApp Business open on their phone until it finishes.',
      showSync: false,
      syncLabel: 'Syncing…',
      progress,
      remaining,
      window: syncWindow,
      status,
    };
  }

  const detail =
    typeof account.history_sync_error === 'string' && account.history_sync_error.trim()
      ? account.history_sync_error.trim()
      : null;

  if (status === 'REFUSED') {
    return {
      tone: 'error',
      headline: 'Meta refused the import request',
      guidance: windowClosed
        ? joinSentences(
            detail ? `Meta reported: ${detail}` : 'The request did not reach Meta',
            'The 24-hour import window has since closed, so this request cannot be retried',
          ) + ` To import the history: ${RE_ONBOARD_STEPS}`
        : joinSentences(
            detail ? `Meta reported: ${detail}` : 'The request did not reach Meta',
            isRateLimitText(detail)
              ? "Meta's request limit is shared across the whole app, so wait 15-30 minutes before trying again"
              : 'You can try again',
            remaining ? `About ${remaining} of the import window is left` : null,
          ),
      showSync: !windowClosed,
      syncLabel: windowClosed ? '' : 'Try again',
      progress: null,
      remaining,
      window: syncWindow,
      status,
    };
  }

  if (status === 'FAILED') {
    return {
      tone: 'error',
      headline: 'Chat history could not be imported',
      guidance:
        (detail ? `Meta reported: ${detail} ` : 'Meta stopped the import. ') +
        `To try the import again: ${RE_ONBOARD_STEPS}`,
      showSync: false,
      syncLabel: '',
      progress: null,
      remaining,
      window: syncWindow,
      status,
    };
  }

  if (!syncWindow.known) {
    return {
      tone: 'info',
      headline: 'Import chat history from the WhatsApp Business app',
      guidance:
        "Phone chats can be imported once within 24 hours of onboarding. We don't have a record of when that started — try it now.",
      showSync: true,
      syncLabel: 'Sync chat history',
      progress: null,
      remaining: null,
      window: syncWindow,
      status,
    };
  }

  if (windowClosed) return null;

  const urgent = (syncWindow.msRemaining ?? 0) <= 2 * 60 * 60 * 1000;
  return {
    tone: urgent ? 'warning' : 'info',
    headline: urgent
      ? `Only ${remaining} left to import chat history`
      : `Import chat history — ${remaining} left`,
    guidance:
      'This number was connected through Coexistence. Meta allows the conversations already on the phone to be imported once, within 24 hours of onboarding — the last 180 days of one-to-one chats, not group chats. After that the history cannot be brought in.',
    showSync: true,
    syncLabel: 'Sync chat history',
    progress: null,
    remaining,
    window: syncWindow,
    status,
  };
}

export function describeHistorySyncState(
  account: HistorySyncAccountLike | null | undefined,
  now: Date = new Date()
): string | null {
  if (!account || !isHistorySyncEligible(account.connection_type)) return null;

  const status = account.history_sync_status;
  const progress = resolveHistorySyncProgress(account.history_sync_progress);

  if (isHistorySyncComplete(status)) return 'Chat history imported';
  if (isHistorySyncRunning(status)) {
    if (resolveHistorySyncStaleness(account, now).stale) {
      return progress === null ? 'Import stuck — nothing arrived' : `Import stuck at ${progress}%`;
    }
    return progress === null ? 'Importing chat history…' : `Importing chat history — ${progress}%`;
  }
  if (status === 'REFUSED') return 'Chat history import was refused by Meta';
  if (status === 'FAILED') return 'Chat history import failed — reconnect needed';

  const syncWindow = resolveHistorySyncWindow(account.coexistence_onboarded_at, now);
  if (syncWindow.known && syncWindow.expired) return 'Chat history import window closed';
  const remaining = formatWindowRemaining(syncWindow.msRemaining);
  if (remaining) return `Chat history not imported — ${remaining} left`;
  return 'Chat history not imported';
}

export function describeHistorySyncRefusal(code: unknown): string {
  switch (code) {
    case 'window_expired':
      return "Meta's 24-hour import window for this account has closed. Chat history can no longer be brought in.";
    case 'already_completed':
      return 'This account\'s chat history has already been imported.';
    case 'in_flight':
      return 'An import is already running for this account. Give it a few minutes.';
    case 'rate_limited':
      return "Meta's request limit is shared across the whole app and is still in effect. Wait 15-30 minutes before trying again — retrying now keeps it tripped for longer.";
    default:
      return 'Chat history could not be imported right now.';
  }
}
